import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'
import './Citas.css'

// 🚀 Cambiado a la sintaxis nativa de Vite para proteger la URL de producción
const API_BASE_URL = process.env.REACT_APP_API_URL

export default function Citas({ toast, comercioId }) {
  const [citas, setCitas] = useState([])
  const hoy = new Date().toISOString().split('T')[0]
  const [fechaSeleccionada, setFechaSeleccionada] = useState(hoy)
  const [plantillas, setPlantillas] = useState([])
  const [modalConfirmacion, setModalConfirmacion] = useState({ abierto: false, turnoId: null })
  const [estrategiaSeleccionada, setEstrategiaSeleccionada] = useState('')
  
  // 🛡️ Estado para bloquear la UI durante la petición atómica
  const [procesando, setProcesando] = useState(false)

  // Estados para configuración del comercio
  const [anticipacionConfig, setAnticipacionConfig] = useState(30)
  const [maxCitasConfig, setMaxCitasConfig] = useState(1) 

  const cargar = useCallback(async () => {
    if (!comercioId) return
    
    try {
      // 1. TRAER LA CONFIGURACIÓN DEL COMERCIO (Anticipación y Cupo Máximo)
      const { data: dataConfig } = await supabase
        .from('configuracion_comercios')
        .select('minutos_anticipacion_recordatorio, max_citas_por_horario')
        .eq('comercio_id', comercioId)
        .maybeSingle()

      if (dataConfig) {
        if (dataConfig.minutos_anticipacion_recordatorio !== null) {
          setAnticipacionConfig(dataConfig.minutos_anticipacion_recordatorio)
        }
        if (dataConfig.max_citas_por_horario !== null) {
          setMaxCitasConfig(dataConfig.max_citas_por_horario) 
        }
      }

      // 2. TRAER PLANTILLAS DINÁMICAS
      const resPlantillas = await fetch(`${API_BASE_URL}/api/plantillas/${comercioId}`)
      if (resPlantillas.ok) {
        const dataP = await resPlantillas.json()
        setPlantillas(dataP)
      }
    } catch (e) {
      console.error("Error cargando configuraciones iniciales:", e)
    }

    // 3. TRAER TURNOS
    const { data: turnos, error: errorTurnos } = await supabase
      .from('turnos_clientes')
      .select('*')
      .eq('comercio_id', comercioId)
      .eq('tipo_registro', 'cita')
      .order('fecha_turno', { ascending: true })
      
    if (errorTurnos) {
      console.error("Error cargando citas:", errorTurnos)
      toast('Error al sincronizar la agenda', 'error')
      return
    }

    if (!turnos || turnos.length === 0) {
      setCitas([])
      return
    }

    // Mapeo y vinculación con inventario de celulares
    let idsDeCelulares = []
    turnos.forEach(turno => {
      if (turno.celulares_ids && turno.celulares_ids.length > 0) {
        idsDeCelulares.push(...turno.celulares_ids)
      }
    })

    let diccionarioCelulares = {}
    if (idsDeCelulares.length > 0) {
      const idsUnicos = [...new Set(idsDeCelulares)]
      const { data: celulares } = await supabase
        .from('inventario_celulares')
        .select('id, modelo, color, capacidad, bateria, estado_estetico, estado_venta')
        .in('id', idsUnicos)
        
      if (celulares) {
        celulares.forEach(cel => { diccionarioCelulares[cel.id] = cel })
      }
    }

    const turnosCompletos = turnos.map(turno => {
      const primerCelularId = turno.celulares_ids && turno.celulares_ids.length > 0 ? turno.celulares_ids[0] : null
      return {
        ...turno,
        inventario_celulares: primerCelularId ? diccionarioCelulares[primerCelularId] : null
      }
    })

    setCitas(turnosCompletos)
  }, [comercioId, toast])

  useEffect(() => { cargar() }, [cargar])

  useEffect(() => {
    if (!comercioId) return
    const canalCitas = supabase.channel('citas-en-vivo')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'turnos_clientes', filter: `comercio_id=eq.${comercioId}` },
        () => { cargar() }
      ).subscribe()
    return () => supabase.removeChannel(canalCitas)
  }, [comercioId, cargar])

  const ejecutarCompletarTurno = async () => {
    if (procesando) return 
    setProcesando(true)
    
    const turnoId = modalConfirmacion.turnoId
    
    try {
      toast('Procesando entrega...', 'info')
      const response = await fetch(`${API_BASE_URL}/api/turnos/${turnoId}/completar?estrategia=${estrategiaSeleccionada}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      })
      const resData = await response.json()
      
      if (response.ok) {
        toast('¡Turno completado con éxito!', 'success')
        setModalConfirmacion({ abierto: false, turnoId: null }) 
        cargar() 
      } else {
        if (response.status === 400) {
          toast('Este turno ya fue completado previamente.', 'info')
          setModalConfirmacion({ abierto: false, turnoId: null })
          cargar()
        } else {
          toast(resData.detail || 'Error al completar el turno', 'error')
        }
      }
    } catch (error) {
      toast('Error de conexión con el servidor', 'error')
    } finally {
      setProcesando(false) 
    }
  }

  const cambiarDia = (dias) => {
    const nuevaFecha = new Date(fechaSeleccionada)
    nuevaFecha.setDate(nuevaFecha.getDate() + dias)
    setFechaSeleccionada(nuevaFecha.toISOString().split('T')[0])
  }

  const calcularHoraEnvioPrevista = (fechaTurnoString, minutosAnticipacion) => {
    if (!fechaTurnoString) return '—'
    try {
      const fechaLimpia = fechaTurnoString.includes(' ') ? fechaTurnoString.replace(' ', 'T') : fechaTurnoString
      const fechaTurno = new Date(fechaLimpia)
      const fechaEnvio = new Date(fechaTurno.getTime() - minutosAnticipacion * 60 * 1000)
      return fechaEnvio.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    } catch (e) {
      return '—'
    }
  }

  const citasDelDia = citas.filter(c => c.fecha_turno?.startsWith(fechaSeleccionada))
  const initials = (nombre) => nombre ? nombre.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?'
  
  const extraerHora = (fechaCompleta) => {
    if (!fechaCompleta) return '—'
    const partes = fechaCompleta.split(' ')
    if (partes.length > 1) return partes[1].slice(0, 5)
    return new Date(fechaCompleta).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  // LÓGICA DE AGRUPACIÓN POR HORARIO
  const citasAgrupadas = citasDelDia.reduce((acc, cita) => {
    const hora = extraerHora(cita.fecha_turno)
    if (!acc[hora]) acc[hora] = []
    acc[hora].push(cita)
    return acc
  }, {})

  const Etiqueta = ({ texto }) => texto ? <span className="citas-etiqueta">{texto}</span> : null

  const abrirModalConfirmacion = (id) => {
    if (plantillas.length > 0) setEstrategiaSeleccionada(plantillas[0].id)
    else setEstrategiaSeleccionada('satisfaccion')
    setModalConfirmacion({ abierto: true, turnoId: id })
  }

  return (
    <div className="citas-container">
      <div className="citas-header">
        <div className="citas-title-group">
          <h1 className="citas-title">Agenda Diaria</h1>
          <p className="citas-subtitle">Administrá los turnos e ingresos de equipos</p>
        </div>

        <div className="citas-date-picker">
          <button onClick={() => cambiarDia(-1)} className="btn-date-nav">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
          </button>
          <input type="date" value={fechaSeleccionada} onChange={(e) => setFechaSeleccionada(e.target.value)} className="input-date" />
          <button onClick={() => cambiarDia(1)} className="btn-date-nav">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </button>
        </div>
      </div>

      <div className="citas-card" style={{ padding: 0, backgroundColor: 'transparent', border: 'none', boxShadow: 'none' }}>
        {Object.keys(citasAgrupadas).length === 0 ? (
          <div className="citas-empty" style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="citas-empty-icon">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            <p className="citas-empty-title">Sin turnos programados</p>
          </div>
        ) : (
          <div className="citas-list">
            {/* RENDERIZADO POR GRUPOS HORARIOS */}
            {Object.keys(citasAgrupadas).sort().map(hora => {
              const grupo = citasAgrupadas[hora]
              const cupoLleno = grupo.length >= maxCitasConfig

              return (
                <div key={hora} style={{ marginBottom: '1.5rem', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                  
                  {/* Cabecera del Bloque Horario */}
                  <div style={{ backgroundColor: '#f8fafc', padding: '12px 18px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifycontent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                      <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0f172a' }}>{hora} hs</span>
                      <span style={{
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        backgroundColor: cupoLleno ? '#fee2e2' : '#e0f2fe',
                        color: cupoLleno ? '#991b1b' : '#0369a1',
                      }}>
                        {grupo.length} / {maxCitasConfig} {maxCitasConfig === 1 ? 'cupo ocupado' : 'cupos ocupados'} {cupoLleno && '(Lleno)'}
                      </span>
                    </div>
                  </div>

                  {/* Lista de clientes dentro de ese horario */}
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {grupo.map((c, index) => {
                      const cel = c.inventario_celulares || { modelo: `Equipo no vinculado (ID #${c.celulares_ids?.[0] || '?'})`, estado_venta: 'desconocido' }
                      
                      return (
                        <div key={c.id} style={{ 
                          padding: '16px 18px', 
                          borderBottom: index !== grupo.length - 1 ? '1px solid #e2e8f0' : 'none',
                          display: 'flex',
                          gap: '16px'
                        }}>
                          <div className="cita-content" style={{ width: '100%' }}>
                            <div className="cita-top-row">
                              <div className="cita-client-info">
                                <div className="cita-avatar">{initials(c.cliente_nombre)}</div>
                                <div>
                                  <div className="cita-client-name">{c.cliente_nombre || 'Cliente anónimo'}</div>
                                  <div className="cita-client-phone" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginTop: '4px' }}>
                                    <span style={{ color: '#64748b', fontSize: '0.9rem' }}>{c.telefono}</span>
                                    
                                    <span style={{ 
                                      fontSize: '0.7rem', 
                                      padding: '3px 10px', 
                                      borderRadius: '6px', 
                                      backgroundColor: c.recordatorio_enviado ? '#e6f4ea' : '#eff6ff', 
                                      color: c.recordatorio_enviado ? '#137333' : '#1e40af',
                                      fontWeight: '600',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.03em',
                                      border: `1px solid ${c.recordatorio_enviado ? '#ceead6' : '#bfdbfe'}`
                                    }}>
                                      {c.recordatorio_enviado ? 'Aviso Enviado' : 'Aviso Pendiente'}
                                    </span>

                                    {!c.recordatorio_enviado && (
                                      <span style={{ fontSize: '0.78rem', color: '#64748b', fontStyle: 'italic' }}>
                                        (Envío auto: {calcularHoraEnvioPrevista(c.fecha_turno, anticipacionConfig)} hs)
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="cita-actions" style={{ display: 'flex', alignItems: 'center' }}>
                                {cel.estado_venta === 'vendido' ? (
                                  <span style={{ color: '#137333', fontWeight: 'bold', marginRight: '15px', fontSize: '0.9rem' }}>
                                    Entregado
                                  </span>
                                ) : (
                                  <button 
                                    onClick={() => abrirModalConfirmacion(c.id)} 
                                    disabled={procesando}
                                    className="btn-date-nav" 
                                    style={{ backgroundColor: '#007bff', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', marginRight: '10px', cursor: procesando ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: '500', opacity: procesando ? 0.6 : 1 }}
                                  >
                                    Completar Entrega
                                  </button>
                                )}
                                <a href={`https://wa.me/${c.telefono?.replace(/\D/g,'')}`} target="_blank" rel="noreferrer" className="btn-whatsapp">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
                                  WhatsApp
                                </a>
                              </div>
                            </div>
                            <div className="cita-device-row" style={{ marginTop: '12px' }}>
                              <span className="cita-device-model">{cel.modelo}</span>
                              {c.inventario_celulares && (
                                <div className="cita-tags-group">
                                  <Etiqueta texto={cel.capacidad ? `${cel.capacidad} GB` : null} />
                                  <Etiqueta texto={cel.color} />
                                  <Etiqueta texto={cel.bateria ? `Batería: ${cel.bateria} ` : null} />
                                  <Etiqueta texto={cel.estado_estetico} />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {modalConfirmacion.abierto && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: '#ffffff', padding: '30px', borderRadius: '16px', maxWidth: '440px', width: '90%', textAlign: 'left', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <div style={{ width: '56px', height: '56px', backgroundColor: '#e0f2fe', color: '#0284c7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px auto' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
            </div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '1.25rem', fontWeight: '600', color: '#1e293b', textAlign: 'center' }}>¿Confirmás la entrega del equipo?</h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '0.92rem', color: '#64748b', lineHeight: '1.5', textAlign: 'center' }}>Esta acción liquidará el stock del celular y programará la automatización en la cola de fidelización post-venta.</p>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Estrategia Post-Venta (WhatsApp Bot)</label>
              <select 
                value={estrategiaSeleccionada} 
                onChange={(e) => setEstrategiaSeleccionada(e.target.value)} 
                disabled={procesando}
                style={{ width: '100%', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '10px 12px', fontSize: '0.9rem', color: '#334155', backgroundColor: '#f8fafc', outline: 'none', opacity: procesando ? 0.6 : 1 }}
              >
                {plantillas.map(p => <option key={p.id} value={p.id}>{p.nombre} (A los {p.dias_espera} días)</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button 
                onClick={() => setModalConfirmacion({ abierto: false, turnoId: null })} 
                disabled={procesando}
                style={{ padding: '10px 18px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#ffffff', color: '#334155', fontSize: '0.9rem', cursor: procesando ? 'not-allowed' : 'pointer', opacity: procesando ? 0.6 : 1 }}
              >
                Cancelar
              </button>
              <button 
                onClick={ejecutarCompletarTurno} 
                disabled={procesando}
                style={{ padding: '10px 18px', borderRadius: '8px', border: 'none', backgroundColor: procesando ? '#94a3b8' : '#007bff', color: '#ffffff', fontSize: '0.9rem', cursor: procesando ? 'not-allowed' : 'pointer', transition: 'background-color 0.2s' }}
              >
                {procesando ? 'Procesando...' : 'Sí, confirmar entrega'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}